'use client';

import FieldsRepeater from '@components/Form/FieldsRepeater';
import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { fileToBase64 } from '@/src/utils/fileToBase64';
import Confirmation from '@components/Confirmation';
import { LuImageMinus } from 'react-icons/lu';
import ModalTitle from '@components/ModalTitle';
import { useSendMailMutation } from '@api/api';
import { useLocaleRoutes } from '@/src/utils/useLocaleRoutes';
import LoadingBlock from '@components/loadingSkeletons/loadingBlock';
import useParamsEditor from '@/src/utils/useParamsEditor';
import { isErrorWithDataMessage, isErrorWithMessage } from '@api/errorTypes';

interface FileType {
  key: number;
  file: File;
  base64: string;
}

export default function ComplaintForm() {
  const t = useTranslations('generalForms');
  const g = useTranslations('general');
  const editParam = useParamsEditor();
  const path = useLocaleRoutes();
  const [id, setNewId] = useState(0);
  const [files, setFiles] = useState([] as FileType[]);
  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
  } = useForm();
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      let newFiles = [] as FileType[];
      let newId = id;
      acceptedFiles.forEach(async (file: File) => {
        try {
          const base64String = await fileToBase64(file);
          let newFile = {
            key: newId,
            file,
            base64: base64String,
          };
          newFiles = [...newFiles, newFile];
          newId++;
          setFiles([...files, ...newFiles]);
          setNewId(newId);
        } catch (error) {
          console.error('Error converting file to base64:', error);
        }
      });
    },
    [setFiles, setNewId, id, files]
  );
  const { fileRejections, getRootProps, getInputProps, isDragActive } =
    useDropzone({
      onDrop,
      accept: {
        'image/jpeg': [],
        'image/png': [],
      },
    });
  const inputClasses = 'text-black w-full block py-2 px-3';

  const removeFile = (key: number) => {
    let currentFiles = [...files];
    let updatedFiles = currentFiles.filter(
      (file: FileType) => file.key !== key
    );
    setFiles(updatedFiles);
  };

  const acceptedFileItems = files.map((file: FileType) => (
    <li key={file.key}>
      <div className='mb-2 flex items-center justify-between'>
        {file.file.name}
        <Confirmation
          onConfirm={() => removeFile(file.key)}
          title={g('remove')}
          className='rounded-md bg-[#E11] p-2 text-xl text-black hover:bg-white focus:bg-white'
        >
          <LuImageMinus />
        </Confirmation>
      </div>
    </li>
  ));

  const fileRejectionItems = fileRejections.map(({ file, errors }) => (
    <li key={file.name}>
      {file.name} - {file.size} bytes
      <ul>
        {errors.map((e) => (
          <li key={e.code}>{e.message}</li>
        ))}
      </ul>
    </li>
  ));

  const [sendComplaint, { isLoading, isSuccess, isError, error }] =
    useSendMailMutation();

  const submitComplaint = async (formData: any) => {
    let message = '';
    Object.keys(formData).forEach((key: keyof typeof formData) => {
      message = `${message}<br>${t(String(key))}: ${formData[key]}`;
    });
    formData.files = [];
    files.forEach((file: FileType) => {
      formData.files.push({
        name: file.file.name,
        content: file.base64,
      });
    });
    sendComplaint({
      reply: formData.email,
      subject: 'Complaint',
      content: message,
      files: formData.files,
    });
  };

  return (
    <div className='m-auto w-[500px] max-w-full'>
      <ModalTitle title={t('complaintTitle')} />
      {isSuccess && (
        <>
          <p>{t('messageSend')}</p>
          <button
            onClick={() => editParam('modal')}
            className='mt-4 rounded-sm border border-brand bg-brand px-4 py-2 text-center font-semibold uppercase text-dark transition-all hover:bg-transparent hover:text-white'
            type='button'
          >
            {g('close')}
          </button>
        </>
      )}
      {isLoading && <LoadingBlock />}
      {!isLoading && isErrorWithDataMessage(error) && <>{error.data.message}</>}
      {!isLoading && isErrorWithMessage(error) && <>{error.message}</>}
      {!isLoading && !isSuccess && (
        <form
          method='post'
          onSubmit={handleSubmit(submitComplaint)}
          className={`${isLoading ? 'hidden' : ''} flex w-full flex-col gap-2`}
        >
          <FieldsRepeater
            errors={errors}
            register={register}
            t={t}
            setValue={setValue}
            trigger={trigger}
            inputClasses={inputClasses}
            data={{}}
            fields={[
              {
                name: 'type',
                label: `${t('company')} / ${t('private')}`,
                required: true,
                values: [
                  {
                    value: 'Företag',
                    label: t('company'),
                  },
                  {
                    value: 'Privatperson',
                    label: t('private'),
                  },
                ],
              },
              {
                name: 'name',
                label: `${t('name')} / ${t('companyName')}`,
                required: true,
              },
              {
                name: 'phone',
                required: true,
              },
              {
                name: 'email',
                required: true,
                type: 'email',
              },
              {
                name: 'object',
                type: 'textarea',
                required: true,
              },
              {
                name: 'problemDescription',
                type: 'textarea',
                required: true,
              },
              {
                name: 'read',
                label: (
                  <span>
                    {t.rich('read', {
                      a: (chunks) => (
                        <a
                          className='underline'
                          target='_blank'
                          href={`/${path('terms')}`}
                        >
                          {chunks}
                        </a>
                      ),
                    })}
                  </span>
                ),
                required: true,
                values: [g('yes')],
                type: 'boolean',
              },
              {
                name: 'itemPickedUp',
                required: true,
                values: [g('no'), g('yes')],
              },
            ]}
          />
          <div
            {...getRootProps()}
            className='mt-4 flex justify-center rounded-sm border border-brand p-10'
          >
            <input {...getInputProps({ id: 'filesinput' })} />
            {t('files')}
          </div>
          {!!files.length && (
            <ul className='flex flex-col'>{acceptedFileItems}</ul>
          )}
          {!!fileRejections.length && (
            <>
              <h4>{t('rejectedFiles')}</h4>
              <ul>{fileRejectionItems}</ul>
            </>
          )}
          <button
            className='mt-4 rounded-sm border border-brand bg-brand px-4 py-2 text-center font-semibold uppercase text-dark transition-all hover:bg-transparent hover:text-white'
            type='submit'
          >
            {g('submit')}
          </button>
        </form>
      )}
    </div>
  );
}
